import { defineComponent, useListeners, ref, computed, inject, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useLocalizationSupport } from '../../composables/localization-support/index.js';
import { useTableRow } from '../../composables/table-row/index.js';
import accessibility from 'highcharts/modules/accessibility.js';
import Highcharts from 'highcharts';
import itemSeriesModule from 'highcharts/modules/item-series.js';
import HoistComponent from '../hoist-component/HoistComponent.vue.js';
import UiValue from '../ui-value/UiValue.vue.js';
import UiName from '../ui-name/UiName.vue.js';
import set from 'lodash.set';
import { showNoResults, showResults } from '../../helpers/data.js';
import { wrapChartOnRender } from '../../helpers/component.js';

var _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "UiBreakdown",
  props: {
    locale: { type: null, required: true },
    chartType: { type: null, required: false, default: "pie" },
    chartOptions: { type: null, required: false },
    items: { type: Array, required: false },
    highlightMinWeight: { type: Number, required: false },
    highlightMaxItems: { type: Number, required: false },
    highlightMode: { type: null, required: false, default: "detail" },
    tableDisplayOthers: { type: Boolean, required: false, default: false },
    chartDisplayOthers: { type: Boolean, required: false, default: true },
    legendType: { type: null, required: false, default: "topPositions" },
    translationKeys: { type: null, required: false }
  },
  emits: ["table-row-key-click", "table-row-click", "chart-point-click"],
  setup(__props, { expose, emit: emits }) {
    const props = __props;
    accessibility(Highcharts);
    const listeners = useListeners();
    const CHART_POINT_CLICK_EVENT = "chart-point-click";
    const colorClasses = Array.from({ length: 11 }, (_, index) => `fds-ui-breakdown__value--border-color-${index + 1}`);
    const hasChartPointClickListener = Object.prototype.hasOwnProperty.call(listeners, CHART_POINT_CLICK_EVENT);
    const chartElement = ref();
    const uiBreakdownValuesElement = ref(null);
    const isLoadingChartData = ref(false);
    let chart = null;
    const headers = computed(() => {
      var _a, _b, _c, _d, _e, _f;
      return [
        { key: "weight", label: (_c = (_b = (_a = props.translationKeys) == null ? void 0 : _a.table) == null ? void 0 : _b.weight) != null ? _c : "uiBreakdown.table.weight" },
        { key: "name", label: (_f = (_e = (_d = props.translationKeys) == null ? void 0 : _d.table) == null ? void 0 : _e.name) != null ? _f : "uiBreakdown.table.name" }
      ];
    });
    const labels = computed(() => {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o;
      return {
        caption: (_c = (_b = (_a = props.translationKeys) == null ? void 0 : _a.table) == null ? void 0 : _b.caption) != null ? _c : "uiBreakdown.table.caption",
        noItems: (_f = (_e = (_d = props.translationKeys) == null ? void 0 : _d.table) == null ? void 0 : _e.noItems) != null ? _f : "uiBreakdown.table.noItems",
        counting: (_i = (_h = (_g = props.translationKeys) == null ? void 0 : _g.chart) == null ? void 0 : _h.counting) != null ? _i : "uiBreakdown.chart.counting",
        others: (_l = (_k = (_j = props.translationKeys) == null ? void 0 : _j.chart) == null ? void 0 : _k.others) != null ? _l : "uiBreakdown.chart.others",
        topPositions: (_o = (_n = (_m = props.translationKeys) == null ? void 0 : _m.chart) == null ? void 0 : _n.topPositions) != null ? _o : "uiBreakdown.chart.topPositions"
      };
    });
    const localizationSupport = useLocalizationSupport(props, {
      translator: inject("translator"),
      numberFormatter: inject("numberFormatter"),
      dateFormatter: inject("dateFormatter")
    });
    const { translate, textFormat, numberFormat } = localizationSupport;
    const tableRow = useTableRow(listeners, emits);
    const { tableRowAttributes, hasTableRowClickListener, hasTableRowKeyClickListener, tableRowEvents, onTableRowKeyClick } = tableRow;
    const highlightedItems = computed(() => {
      if (!props.items) {
        return [];
      }
      let items = Array.from(props.items).filter((item) => item.valueRelative);
      if (props.highlightMinWeight) {
        items = items.filter((item) => item.valueRelative && props.highlightMinWeight && item.valueRelative >= props.highlightMinWeight);
      }
      if (props.highlightMaxItems) {
        const limit = props.highlightMaxItems >= colorClasses.length ? colorClasses.length - 1 : props.highlightMaxItems;
        items = items.splice(0, limit);
      }
      return items;
    });
    const tableItems = computed(() => {
      var _a;
      if (props.tableDisplayOthers && hasOthers.value) {
        return [
          ...highlightedItems.value,
          {
            name: translate(labels.value.others),
            valueRelative: otherItemsTotalValue.value
          }
        ];
      }
      return (_a = props.items) != null ? _a : [];
    });
    const legendItems = computed(() => {
      if (!hasHighlightedItems.value) {
        return [];
      }
      if (props.legendType === "counting") {
        return [
          {
            name: translate(labels.value.counting),
            valueRelative: highlightedItems.value.length
          }
        ];
      }
      if (props.legendType === "topPositions") {
        const topPositions = [
          {
            name: translate(labels.value.topPositions),
            valueRelative: highlightedItemsTotalValue.value
          }
        ];
        if (props.chartDisplayOthers && hasOthers.value) {
          topPositions.push({
            name: translate(labels.value.others),
            valueRelative: otherItemsTotalValue.value
          });
        }
        return topPositions;
      }
      return [];
    });
    const legendItemNumberFormatOptions = computed(() => {
      if (props.legendType === "topPositions") {
        return { type: "absolutePercentage" };
      }
      if (props.legendType === "counting") {
        return { type: "integer" };
      }
      return void 0;
    });
    const highlightedItemsTotalValue = computed(() => {
      return highlightedItems.value.reduce((a, b) => {
        var _a;
        return a + ((_a = b.valueRelative) != null ? _a : 0);
      }, 0);
    });
    const otherItemsTotalValue = computed(() => {
      return 1 - highlightedItemsTotalValue.value;
    });
    const seriesDataTotalValue = computed(() => {
      if (!hasHighlightedItems.value) {
        return 0;
      }
      if (props.chartDisplayOthers) {
        return 1;
      }
      const chartItems = getChartItems();
      return chartItems.reduce((a, b) => {
        var _a;
        return a + ((_a = b.valueRelative) != null ? _a : 0);
      }, 0);
    });
    const renderChart = () => {
      isLoadingChartData.value = true;
      const chartConfig = getChartConfig();
      const seriesData = getSeriesData();
      if (props.chartType === "stackedBar") {
        set(chartConfig, "series", seriesData);
        set(chartConfig, "yAxis.max", seriesDataTotalValue.value);
      } else {
        set(chartConfig, "series[0].data", seriesData);
      }
      if (chartElement.value && !chart && seriesData.length > 0) {
        chart = Highcharts.chart(chartElement.value, chartConfig);
      } else if (seriesData.length === 0) {
        onRender();
      }
    };
    const onRender = () => {
      isLoadingChartData.value = false;
    };
    const getChartConfig = () => {
      var _a;
      const chartOptionCallback = (_a = props.chartOptions) == null ? void 0 : _a[props.chartType];
      const options = typeof chartOptionCallback === "function" ? chartOptionCallback({
        seriesDataTotalValue: seriesDataTotalValue.value,
        localizationSupport
      }) : {};
      if (hasChartPointClickListener) {
        const pointListener = function(event) {
          const pointName = props.chartType === "stackedBar" ? this.series.name : this.name;
          emits(CHART_POINT_CLICK_EVENT, pointName);
        };
        set(options, "plotOptions.series.point.events.click", pointListener);
      }
      return wrapChartOnRender(options, onRender);
    };
    const getItemHighlightClass = (item) => {
      const highlightedItemIndex = getHighlightedItemIndex(item);
      if (isHighlightedOtherItem(item, highlightedItemIndex)) {
        return {
          [seriesOthersColorClass.value]: true
        };
      }
      if (highlightedItemIndex < 0) {
        return {};
      }
      return {
        [getSeriesDataColorClass(highlightedItemIndex)]: true
      };
    };
    const getHighlightedItemIndex = (item) => {
      const highlightedItemIndex = highlightedItems.value.findIndex((highlightedItem) => highlightedItem.name === item.name);
      if (highlightedItemIndex < 0) {
        return -1;
      }
      if (props.highlightMode === "topPositions") {
        return 0;
      }
      return highlightedItemIndex;
    };
    const isHighlightedOtherItem = (item, highlightedItemIndex) => {
      return highlightedItemIndex < 0 && item.valueRelative !== null && item.valueRelative > 0;
    };
    const getChartItems = () => {
      return props.highlightMode === "topPositions" ? getTopPositionItems() : highlightedItems.value.slice(0, colorClasses.length - 1);
    };
    const getTopPositionItems = () => {
      if (highlightedItemsTotalValue.value === 0) {
        return [];
      }
      return [
        {
          name: translate(labels.value.topPositions),
          valueRelative: highlightedItemsTotalValue.value
        }
      ];
    };
    const getSeriesData = () => {
      if (!hasHighlightedItems.value) {
        return [];
      }
      const chartItems = getChartItems();
      if (props.chartType === "stackedBar") {
        return getSeriesOptionsData(chartItems);
      }
      return getPointOptionsData(chartItems);
    };
    const getItemsSortedByRemainder = (roundedPointOptionsData) => {
      return [...roundedPointOptionsData].sort((a, b) => {
        const remainderA = a.y ? a.y % 100 : 0;
        const remainderB = b.y ? b.y % 100 : 0;
        return remainderA > remainderB ? -1 : 1;
      });
    };
    const getItemsSortedByValue = (sortedByRemainder) => {
      return [...sortedByRemainder].sort((a, b) => {
        var _a, _b;
        if (a.name === translate(labels.value.others))
          return 1;
        if (b.name === translate(labels.value.others))
          return -1;
        const valueA = (_a = a.y) != null ? _a : 0;
        const valueB = (_b = b.y) != null ? _b : 0;
        return valueA > valueB ? -1 : 1;
      });
    };
    const getItemChartPointOptionsData = (pointOptionsData) => {
      const fractionalTotal = pointOptionsData.reduce((a, b) => {
        var _a;
        return a + ((_a = b.y) != null ? _a : 0);
      }, 0);
      const roundedPointOptionsData = pointOptionsData.map((item) => {
        return {
          ...item,
          y: item.y ? item.y * 100 / fractionalTotal : item.y
        };
      });
      const chartMaximumValue = 100;
      const pointOptionsTotal = roundedPointOptionsData.reduce((a, b) => {
        var _a;
        return a + Math.trunc((_a = b.y) != null ? _a : 0);
      }, 0);
      const pointOptionDifference = chartMaximumValue - pointOptionsTotal;
      const sortedByRemainder = getItemsSortedByRemainder(roundedPointOptionsData);
      for (let i = pointOptionDifference; i > 0; i--) {
        const pointOption = sortedByRemainder[i % sortedByRemainder.length];
        pointOption.y = pointOption.y ? pointOption.y + 1 : pointOption.y;
      }
      const sortedByValue = getItemsSortedByValue(sortedByRemainder);
      return sortedByValue.map((item) => ({
        ...item,
        y: item.y ? Math.trunc(item.y) : item.y
      }));
    };
    const getPointOptionsData = (chartItems) => {
      const data = chartItems.map((chartItem, index) => ({
        y: chartItem.valueRelative,
        name: textFormat(chartItem.name),
        custom: {
          label: numberFormat(chartItem.valueRelative, { type: "absolutePercentage" })
        }
      }));
      if (hasHighlightedItems.value && props.chartDisplayOthers && hasOthers.value) {
        data.push({
          y: otherItemsTotalValue.value,
          name: translate(labels.value.others),
          className: "fds-ui-breakdown__others",
          custom: {
            label: numberFormat(otherItemsTotalValue.value, { type: "absolutePercentage" })
          }
        });
      }
      if (props.chartType === "item" && data.length > 0) {
        return getItemChartPointOptionsData(data);
      }
      return data;
    };
    const getSeriesOptionsData = (items) => {
      const data = items.map((item, index) => ({
        data: [{ y: item.valueRelative }],
        name: textFormat(item.name),
        type: "bar",
        custom: {
          label: numberFormat(item.valueRelative, { type: "absolutePercentage" })
        }
      }));
      if (hasHighlightedItems.value && props.chartDisplayOthers && hasOthers.value) {
        data.push({
          data: [
            {
              y: otherItemsTotalValue.value,
              className: "fds-ui-breakdown__others"
            }
          ],
          name: translate(labels.value.others),
          type: "bar",
          custom: {
            label: numberFormat(otherItemsTotalValue.value, { type: "absolutePercentage" })
          }
        });
      }
      return data;
    };
    const getSeriesDataColorClass = (index) => {
      var _a, _b;
      const baseColor = colorClasses.slice(-1)[0];
      return (_b = (_a = colorClasses[index]) != null ? _a : baseColor) != null ? _b : "";
    };
    const seriesOthersColorClass = computed(() => {
      return colorClasses[colorClasses.length - 1];
    });
    const hasHighlightedItems = computed(() => {
      return highlightedItems.value.length > 0;
    });
    const hasOthers = computed(() => {
      const roundedOthersTotal = Math.round(otherItemsTotalValue.value * 100) / 100;
      return roundedOthersTotal > 0;
    });
    watch(
      () => [highlightedItems.value, props.highlightMode, props.chartType],
      () => {
        if (chart) {
          chart.destroy();
          chart = null;
        }
        nextTick(() => {
          renderChart();
        });
      }
    );
    const onItemHover = (item) => {
      var _a, _b;
      const highlightedItemIndex = getHighlightedItemIndex(item);
      const points = (_a = chart == null ? void 0 : chart.series[0].points) != null ? _a : [];
      if (highlightedItemIndex >= 0 && ((_b = points[highlightedItemIndex]) == null ? void 0 : _b.y)) {
        points[highlightedItemIndex].setState("hover");
        return;
      }
      if (highlightedItemIndex < 0 && points.length > 0 && hasOthers.value) {
        points[points.length - 1].setState("hover");
      }
    };
    const onItemHoverLeave = () => {
      chart == null ? void 0 : chart.series[0].points.forEach((point) => {
        point.setState("");
      });
    };
    onMounted(() => {
      itemSeriesModule(Highcharts);
      renderChart();
    });
    onBeforeUnmount(() => {
      chart == null ? void 0 : chart.destroy();
      chart = null;
    });
    expose({
      getItemHighlightClass,
      getItemChartPointOptionsData,
      chart,
      chartElement,
      highlightedItems
    });
    return { __sfc: true, props, emits, listeners, CHART_POINT_CLICK_EVENT, colorClasses, hasChartPointClickListener, chartElement, uiBreakdownValuesElement, isLoadingChartData, chart, headers, labels, localizationSupport, translate, textFormat, numberFormat, tableRow, tableRowAttributes, hasTableRowClickListener, hasTableRowKeyClickListener, tableRowEvents, onTableRowKeyClick, highlightedItems, tableItems, legendItems, legendItemNumberFormatOptions, highlightedItemsTotalValue, otherItemsTotalValue, seriesDataTotalValue, renderChart, onRender, getChartConfig, getItemHighlightClass, getHighlightedItemIndex, isHighlightedOtherItem, getChartItems, getTopPositionItems, getSeriesData, getItemsSortedByRemainder, getItemsSortedByValue, getItemChartPointOptionsData, getPointOptionsData, getSeriesOptionsData, getSeriesDataColorClass, seriesOthersColorClass, hasHighlightedItems, hasOthers, onItemHover, onItemHoverLeave, HoistComponent, UiValue, UiName, showNoResults, showResults };
  }
});

export { _sfc_main as default };
